'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Unicode text styler. Every "font" here is a different set of Unicode code
// points (mathematical alphanumerics, enclosed letters, combining marks), not
// a font: that is why the result survives paste into bios and chats, and also
// why the page is honest about screen readers struggling with it.

export type FancyTextVariant =
  | 'fancy-text-generator'
  | 'cursed-text-generator'
  | 'small-text-generator'
  | 'italic-text-generator'
  | 'strikethrough-text-generator'
  | 'cute-font-generator'
  | 'facebook-font-generator'
  | 'twitter-font-generator'
  | 'whatsapp-font-generator'
  | 'discord-fonts-generator'
  | 'invisible-text-generator'
  | 'aesthetic-text-generator'
  | 'mirror-text-generator'
  | 'bubble-text-generator';

type StyleDef = { id: string; label: string; map: (text: string) => string };

function offsetMap(
  upperBase: number,
  lowerBase: number,
  digitBase: number | null,
  exceptions: Record<string, string> = {},
) {
  return (text: string) =>
    Array.from(text)
      .map((ch) => {
        if (exceptions[ch]) return exceptions[ch];
        const code = ch.codePointAt(0)!;
        if (code >= 65 && code <= 90) return String.fromCodePoint(upperBase + (code - 65));
        if (code >= 97 && code <= 122) return String.fromCodePoint(lowerBase + (code - 97));
        if (digitBase != null && code >= 48 && code <= 57)
          return String.fromCodePoint(digitBase + (code - 48));
        return ch;
      })
      .join('');
}

function tableMap(upper: string, lower: string) {
  const uppers = Array.from(upper);
  const lowers = Array.from(lower);
  return (text: string) =>
    Array.from(text)
      .map((ch) => {
        const code = ch.codePointAt(0)!;
        if (code >= 65 && code <= 90) return uppers[code - 65] ?? ch;
        if (code >= 97 && code <= 122) return lowers[code - 97] ?? ch;
        return ch;
      })
      .join('');
}

function combiningMap(mark: string) {
  return (text: string) =>
    Array.from(text)
      .map((ch) => (/\s/.test(ch) ? ch : ch + mark))
      .join('');
}

// Combining marks for zalgo, split by position so intensity reads as "haunted",
// not "unreadable garbage" at the low end.
const ZALGO_UP = ['̀', '́', '̂', '̃', '̆', '̇', '̋', '̏', '̑', '͂'];
const ZALGO_DOWN = ['̖', '̗', '̘', '̙', '̜', '̣', '̤', '̥', '̬', '̰'];

function zalgo(text: string, intensity: number): string {
  return Array.from(text)
    .map((ch) => {
      if (/\s/.test(ch)) return ch;
      let out = ch;
      for (let i = 0; i < intensity; i++) {
        out += ZALGO_UP[Math.floor(Math.random() * ZALGO_UP.length)];
        if (i % 2 === 0) out += ZALGO_DOWN[Math.floor(Math.random() * ZALGO_DOWN.length)];
      }
      return out;
    })
    .join('');
}

const STYLES: StyleDef[] = [
  { id: 'bold', label: 'Bold', map: offsetMap(0x1d400, 0x1d41a, 0x1d7ce) },
  {
    id: 'italic',
    label: 'Italic',
    map: offsetMap(0x1d434, 0x1d44e, null, { h: 'ℎ' }),
  },
  { id: 'bold-italic', label: 'Bold italic', map: offsetMap(0x1d468, 0x1d482, null) },
  {
    id: 'script',
    label: 'Script',
    map: offsetMap(0x1d49c, 0x1d4b6, null, {
      B: 'ℬ',
      E: 'ℰ',
      F: 'ℱ',
      H: 'ℋ',
      I: 'ℐ',
      L: 'ℒ',
      M: 'ℳ',
      R: 'ℛ',
      e: 'ℯ',
      g: 'ℊ',
      o: 'ℴ',
    }),
  },
  {
    id: 'fraktur',
    label: 'Fraktur',
    map: offsetMap(0x1d504, 0x1d51e, null, {
      C: 'ℭ',
      H: 'ℌ',
      I: 'ℑ',
      R: 'ℜ',
      Z: 'ℨ',
    }),
  },
  {
    id: 'double-struck',
    label: 'Double-struck',
    map: offsetMap(0x1d538, 0x1d552, 0x1d7d8, {
      C: 'ℂ',
      H: 'ℍ',
      N: 'ℕ',
      P: 'ℙ',
      Q: 'ℚ',
      R: 'ℝ',
      Z: 'ℤ',
    }),
  },
  { id: 'monospace', label: 'Monospace', map: offsetMap(0x1d670, 0x1d68a, 0x1d7f6) },
  {
    id: 'circled',
    label: 'Circled',
    map: (text) =>
      Array.from(text)
        .map((ch) => {
          const code = ch.codePointAt(0)!;
          if (code >= 65 && code <= 90) return String.fromCodePoint(0x24b6 + (code - 65));
          if (code >= 97 && code <= 122) return String.fromCodePoint(0x24d0 + (code - 97));
          if (code === 48) return '⓪';
          if (code >= 49 && code <= 57) return String.fromCodePoint(0x2460 + (code - 49));
          return ch;
        })
        .join(''),
  },
  {
    id: 'squared',
    label: 'Squared',
    map: (text) =>
      Array.from(text)
        .map((ch) => {
          const code = ch.codePointAt(0)!;
          if (code >= 65 && code <= 90) return String.fromCodePoint(0x1f130 + (code - 65));
          if (code >= 97 && code <= 122) return String.fromCodePoint(0x1f130 + (code - 97));
          return ch;
        })
        .join(''),
  },
  { id: 'fullwidth', label: 'Fullwidth', map: offsetMap(0xff21, 0xff41, 0xff10) },
  {
    id: 'small-caps',
    label: 'Small caps',
    map: tableMap('ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘQʀꜱᴛᴜᴠᴡxʏᴢ', 'ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘqʀꜱᴛᴜᴠᴡxʏᴢ'),
  },
  {
    id: 'superscript',
    label: 'Small (superscript)',
    map: tableMap('ᴬᴮᶜᴰᴱᶠᴳᴴᴵᴶᴷᴸᴹᴺᴼᴾQᴿˢᵀᵁⱽᵂXYZ', 'ᵃᵇᶜᵈᵉᶠᵍʰⁱʲᵏˡᵐⁿᵒᵖqʳˢᵗᵘᵛʷˣʸᶻ'),
  },
  { id: 'strikethrough', label: 'Strikethrough', map: combiningMap('̶') },
  { id: 'underline', label: 'Underline', map: combiningMap('̲') },
  { id: 'slash', label: 'Slashed', map: combiningMap('̸') },
  { id: 'double-strike', label: 'Double strikethrough', map: combiningMap('̶̶') },
  { id: 'cross-out', label: 'Crossed out (X)', map: combiningMap('̽') },
  { id: 'sans-italic', label: 'Sans italic', map: offsetMap(0x1d608, 0x1d622, null) },
  { id: 'sans-bold-italic', label: 'Sans bold italic', map: offsetMap(0x1d63c, 0x1d656, null) },
  { id: 'sans-bold', label: 'Sans bold', map: offsetMap(0x1d5d4, 0x1d5ee, 0x1d7ec) },
  {
    id: 'bubble-black',
    label: 'Black bubbles',
    map: (text) =>
      Array.from(text)
        .map((ch) => {
          const code = ch.codePointAt(0)!;
          if (code >= 65 && code <= 90) return String.fromCodePoint(0x1f150 + (code - 65));
          if (code >= 97 && code <= 122) return String.fromCodePoint(0x1f150 + (code - 97));
          return ch;
        })
        .join(''),
  },
  {
    id: 'aesthetic',
    label: 'Aesthetic (vaporwave)',
    map: (text) => Array.from(offsetMap(0xff21, 0xff41, 0xff10)(text)).join(' '),
  },
  {
    id: 'upside-down',
    label: 'Upside down',
    map: (text) => {
      const m = tableMap('∀ꓭƆꓷƎℲ⅁HIſꓘ˥WNOԀῸꓤS⊥∩ΛMX⅄Z', 'ɐqɔpǝɟƃɥᴉɾʞlɯuodbɹsʇnʌʍxʎz');
      return Array.from(m(text)).reverse().join('');
    },
  },
  {
    id: 'mirror',
    label: 'Mirror (reversed)',
    map: (text) => {
      const m = tableMap('AᗺƆᗡƎꟻᎮHIႱꓘ⅃MИOꟼỌЯƧTUVWXYZ', 'ɒdɔbɘʇϱʜiįʞlmᴎoqpɿƨƚυvwxγz');
      return Array.from(m(text)).reverse().join('');
    },
  },
  { id: 'cute-hearts', label: 'Hearts', map: (text) => `♡ ${text} ♡` },
  {
    id: 'cute-bear',
    label: 'Bear',
    map: (text) => `ʕ•ᴥ•ʔ ${offsetMap(0x1d608, 0x1d622, null)(text)}`,
  },
  { id: 'cute-bow', label: 'Bow', map: (text) => `˚ʚ♡ɞ˚ ${text} ˚ʚ♡ɞ˚` },
  {
    id: 'cute-flowers',
    label: 'Flowers',
    map: (text) =>
      `✿ ${tableMap('ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘQʀꜱᴛᴜᴠᴡxʏᴢ', 'ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘqʀꜱᴛᴜᴠᴡxʏᴢ')(text)} ✿`,
  },
  { id: 'cute-brackets', label: 'Soft brackets', map: (text) => `꒰ ${text} ꒱ ♡` },
  { id: 'cute-stars', label: 'Sparkles', map: (text) => `⋆｡°✩ ${text} ✩°｡⋆` },
];

// Characters that render as blank space but are not ordinary spaces, so apps that reject an
// empty name or message accept them. Support differs per app, hence several options.
const INVISIBLE: Array<{ id: string; label: string; char: string; note: string }> = [
  {
    id: 'hangul',
    label: 'Hangul filler (U+3164)',
    char: 'ㅤ',
    note: 'Works for blank names in many games and apps.',
  },
  {
    id: 'braille',
    label: 'Braille blank (U+2800)',
    char: '⠀',
    note: 'Widest support for blank messages and bios.',
  },
  {
    id: 'zwsp',
    label: 'Zero-width space (U+200B)',
    char: '​',
    note: 'Takes no width at all; some apps strip it.',
  },
  {
    id: 'em-space',
    label: 'Em space (U+2003)',
    char: ' ',
    note: 'A wide space that is not collapsed.',
  },
];

const ITALICS = ['italic', 'sans-italic', 'bold-italic', 'sans-bold-italic', 'script'];
const PLATFORM = [
  'bold',
  'sans-bold',
  'italic',
  'sans-italic',
  'bold-italic',
  'script',
  'fraktur',
  'double-struck',
  'monospace',
  'circled',
  'bubble-black',
  'small-caps',
  'strikethrough',
  'underline',
];

const VARIANT_CONFIG: Record<
  FancyTextVariant,
  {
    heading: string;
    sub: string;
    styleOrder: string[];
    zalgoFirst: boolean;
    invisible?: boolean;
    note?: string;
  }
> = {
  'fancy-text-generator': {
    heading: 'Generate fancy text',
    sub: 'Type once, copy any style. These are Unicode characters, so they survive pasting into bios, chats, and usernames.',
    styleOrder: STYLES.map((style) => style.id),
    zalgoFirst: false,
  },
  'cursed-text-generator': {
    heading: 'Generate cursed text',
    sub: 'Zalgo-style stacked diacritics with an intensity dial, plus the tamer Unicode styles below. Copy and haunt responsibly.',
    styleOrder: ['fraktur', 'double-struck', 'strikethrough', 'squared'],
    zalgoFirst: true,
  },
  'small-text-generator': {
    heading: 'Generate small text',
    sub: 'Superscript and small-caps Unicode alphabets that read as tiny text anywhere you paste them.',
    styleOrder: ['superscript', 'small-caps', 'fullwidth', 'circled'],
    zalgoFirst: false,
  },
  'italic-text-generator': {
    heading: 'Generate italic text',
    sub: 'Serif and sans-serif italics, plain or bold, that stay italic wherever you paste them.',
    styleOrder: ITALICS,
    zalgoFirst: false,
  },
  'strikethrough-text-generator': {
    heading: 'Generate strikethrough text',
    sub: 'A line through every letter, built from combining characters, for places with no formatting.',
    styleOrder: ['strikethrough', 'double-strike', 'slash', 'cross-out', 'underline'],
    zalgoFirst: false,
  },
  'cute-font-generator': {
    heading: 'Generate cute text',
    sub: 'Soft letters wrapped in hearts, bows, flowers and sparkles for bios and captions.',
    styleOrder: [
      'cute-bow',
      'cute-hearts',
      'cute-bear',
      'cute-flowers',
      'cute-brackets',
      'cute-stars',
      'small-caps',
      'script',
      'bubble-black',
    ],
    zalgoFirst: false,
  },
  'facebook-font-generator': {
    heading: 'Fonts for Facebook',
    sub: 'Facebook posts and comments have no bold or italic button; these Unicode styles paste in as styled text.',
    styleOrder: PLATFORM,
    zalgoFirst: false,
    note: 'Facebook shows these in posts, comments, bios and Messenger. Your profile name rejects most of them.',
  },
  'twitter-font-generator': {
    heading: 'Fonts for X (Twitter)',
    sub: 'Bold and italic for posts and your display name, which X itself cannot format.',
    styleOrder: PLATFORM,
    zalgoFirst: false,
    note: 'Most of these letters count as 2 characters toward the 280 limit, and screen readers read them poorly, so keep them to a word or two.',
  },
  'whatsapp-font-generator': {
    heading: 'Fonts for WhatsApp',
    sub: 'Styles WhatsApp’s own formatting cannot do, ready to paste into chats and your About.',
    styleOrder: PLATFORM,
    zalgoFirst: false,
    note: 'WhatsApp also formats natively: *bold*, _italic_, ~strikethrough~ and ```monospace``` around a word.',
  },
  'discord-fonts-generator': {
    heading: 'Fonts for Discord',
    sub: 'Unicode styles for nicknames, server names and bios, where Discord markdown does not work.',
    styleOrder: PLATFORM,
    zalgoFirst: false,
    note: 'In messages Discord has markdown: **bold**, *italic*, ~~strikethrough~~, __underline__ and `code`. Use these styles where markdown is not rendered, like nicknames.',
  },
  'invisible-text-generator': {
    heading: 'Copy invisible text',
    sub: 'Blank characters for empty names, messages and bios in apps that reject a normal space.',
    styleOrder: [],
    zalgoFirst: false,
    invisible: true,
  },
  'aesthetic-text-generator': {
    heading: 'Generate aesthetic text',
    sub: 'Vaporwave-style wide letters, soft small caps and decorated text for bios and captions.',
    styleOrder: [
      'aesthetic',
      'fullwidth',
      'small-caps',
      'script',
      'cute-stars',
      'cute-bow',
      'superscript',
    ],
    zalgoFirst: false,
  },
  'mirror-text-generator': {
    heading: 'Generate mirror text',
    sub: 'Text reversed with mirrored letters, plus upside-down text, from Unicode lookalikes.',
    styleOrder: ['mirror', 'upside-down'],
    zalgoFirst: false,
    note: 'Unicode has no true mirrored alphabet, so a few letters use the closest lookalike.',
  },
  'bubble-text-generator': {
    heading: 'Generate bubble text',
    sub: 'Letters in white and black circles, plus squared letters, from the Unicode enclosed alphabets.',
    styleOrder: ['circled', 'bubble-black', 'squared'],
    zalgoFirst: false,
  },
};

export default function BrowserFancyTextGenerator({ variant }: { variant: FancyTextVariant }) {
  const config = VARIANT_CONFIG[variant];
  const [invisibleCount, setInvisibleCount] = useState(1);
  const usageReported = useRef(false);

  const [input, setInput] = useState('Type something fancy');
  const [zalgoIntensity, setZalgoIntensity] = useState(3);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const byId = new Map(STYLES.map((style) => [style.id, style]));
    const ordered = config.styleOrder
      .map((id) => byId.get(id))
      .filter((style): style is StyleDef => Boolean(style));
    const list = ordered.map((style) => ({
      id: style.id,
      label: style.label,
      text: style.map(input),
    }));
    if (config.zalgoFirst) {
      list.unshift({ id: 'zalgo', label: 'Cursed (zalgo)', text: zalgo(input, zalgoIntensity) });
    }
    return list;
  }, [input, config, zalgoIntensity]);

  async function copyRow(id: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
      if (!usageReported.current) {
        usageReported.current = true;
        // Usage stats only; a copy button must never block on the credits API.
        void consumeToolUsage(variant, '').catch(() => undefined);
      }
    } catch {
      setCopiedId(null);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{config.heading}</h2>
          <p>{config.sub}</p>
        </div>
        <span className="tool-live-badge">Text</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ gap: '0.75rem' }}>
          <label style={{ width: '100%', display: config.invisible ? 'none' : undefined }}>
            Your text
            <input
              maxLength={200}
              onChange={(event) => setInput(event.target.value)}
              style={{ width: '100%' }}
              type="text"
              value={input}
            />
          </label>

          {config.invisible ? (
            <>
              <label style={{ width: '100%' }}>
                How many characters
                <input
                  max={50}
                  min={1}
                  onChange={(event) => setInvisibleCount(Number(event.target.value) || 1)}
                  type="number"
                  value={invisibleCount}
                />
              </label>
              {INVISIBLE.map((item) => (
                <div
                  key={item.id}
                  style={{
                    alignItems: 'center',
                    border: '1px solid var(--border, #2a2f3a)',
                    borderRadius: 10,
                    display: 'flex',
                    gap: '0.75rem',
                    padding: '0.6rem 0.9rem',
                    width: '100%',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <small style={{ display: 'block', opacity: 0.7 }}>{item.label}</small>
                    <span>{item.note}</span>
                    <span
                      aria-label="Preview of the invisible text"
                      style={{
                        background: 'rgba(127,127,127,0.18)',
                        display: 'inline-block',
                        marginLeft: 8,
                        minWidth: 8,
                        whiteSpace: 'pre',
                      }}
                    >
                      {item.char.repeat(invisibleCount)}
                    </span>
                  </div>
                  <button
                    className="button button-ghost"
                    onClick={() => void copyRow(item.id, item.char.repeat(invisibleCount))}
                    type="button"
                  >
                    {copiedId === item.id ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              ))}
            </>
          ) : null}

          {config.zalgoFirst ? (
            <label style={{ width: '100%' }}>
              Curse intensity
              <input
                max={8}
                min={1}
                onChange={(event) => setZalgoIntensity(Number(event.target.value))}
                type="range"
                value={zalgoIntensity}
              />
              <small>
                {zalgoIntensity <= 2
                  ? 'Slightly haunted'
                  : zalgoIntensity <= 5
                    ? 'Properly cursed'
                    : 'Beyond saving'}
              </small>
            </label>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
            {rows.map((row) => (
              <div
                key={row.id}
                style={{
                  alignItems: 'center',
                  border: '1px solid var(--border, #2a2f3a)',
                  borderRadius: 10,
                  display: 'flex',
                  gap: '0.75rem',
                  padding: '0.6rem 0.9rem',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <small style={{ display: 'block', opacity: 0.7 }}>{row.label}</small>
                  <span style={{ fontSize: '1.05rem', overflowWrap: 'anywhere' }}>{row.text}</span>
                </div>
                <button
                  className="button button-ghost"
                  onClick={() => void copyRow(row.id, row.text)}
                  type="button"
                >
                  {copiedId === row.id ? 'Copied!' : 'Copy'}
                </button>
              </div>
            ))}
          </div>

          {config.note ? <p className="tool-live-panel-note">{config.note}</p> : null}
          <p className="tool-live-panel-note">
            These characters are Unicode symbols, not fonts. Screen readers spell many of them out
            letter by letter, so use styled text for accents like a username or a headline, not for
            whole paragraphs.
          </p>
        </div>
      </div>
    </article>
  );
}
